import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { getDashboard } from '../../api/dashboard'
import { getUserNotifications, markAllNotificationsRead, UserNotification } from '../../api/userNotifications'
import { getLatestAnnouncement } from '../../api/broadcast'
import { getProducts, getCategories } from '../../api/products'
import { getSettings } from '../../api/settings'
import BottomNav from '../../components/BottomNav'

const API = import.meta.env.VITE_API_URL ?? ''

// ── Tokens ────────────────────────────────────────────────────
const G   = '#C89B4C'
const BD  = 'rgba(255,255,255,0.07)'
const TX  = '#F4F1EA'
const TX2 = '#A8A8B0'
const TX3 = '#5E6070'
// surface-1 from HTML reference: translucent gradient + blur (glassmorphism)
const GLASS = {
  background: 'linear-gradient(155deg, rgba(255,248,235,0.05) 0%, rgba(255,248,235,0.015) 100%)',
  backdropFilter: 'blur(11px) saturate(150%)',
  WebkitBackdropFilter: 'blur(11px) saturate(150%)',
} as const
const MONO = "'JetBrains Mono', monospace"

const TIER_CFG = {
  bronze: { label: 'Bronze', next: 'Gold',   color: '#CD7F32', pct: 0.3, nextScore: 40 },
  silver: { label: 'Silver', next: 'Gold',   color: '#A8B8CC', pct: 0.6, nextScore: 70 },
  gold:   { label: 'Gold',   next: null,      color: G,          pct: 1.0, nextScore: 100 },
}

const t = (lang: string) => (ru: string, uz: string) => lang === 'uz' ? uz : ru

// ── Notification Drawer ────────────────────────────────────────
function NotifDrawer({ open, onClose, notifs, lang, onNotifClick, onMarkAll, unread, loading }: {
  open: boolean; onClose: () => void; notifs: UserNotification[]
  lang: string; onNotifClick: (n: UserNotification) => void
  onMarkAll: () => void; unread: number; loading: boolean
}) {
  const tr = t(lang)
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.60)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none', transition: 'opacity 0.22s',
      }} />
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 101,
        background: '#0D1526', borderRadius: '20px 20px 0 0',
        border: `1px solid ${BD}`, borderBottom: 'none',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        transform: open ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.28s cubic-bezier(0.32,0.72,0,1)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 99, background: BD }} />
        </div>
        <div style={{ padding: '12px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${BD}` }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TX }}>
            {tr('Уведомления', 'Bildirishnomalar')}
            {unread > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: G, background: 'rgba(200,155,76,0.14)', border: `1px solid rgba(200,155,76,0.30)`, borderRadius: 99, padding: '1px 7px' }}>{unread}</span>}
          </span>
          {unread > 0 && <button onClick={onMarkAll} style={{ background: 'none', border: 'none', fontSize: 12, color: G, cursor: 'pointer' }}>{tr("Прочитать все", "Barchasini o'qish")}</button>}
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0 40px' }}>
          {loading && <p style={{ textAlign: 'center', color: TX3, padding: '30px 0' }}>…</p>}
          {!loading && notifs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <p style={{ fontSize: 36 }}>🔔</p>
              <p style={{ fontSize: 13, color: TX3 }}>{tr('Нет уведомлений', "Bildirishnomalar yo'q")}</p>
            </div>
          )}
          {notifs.map(n => {
            const title = lang === 'uz' ? n.title_uz : n.title_ru
            const body  = lang === 'uz' ? n.body_uz  : n.body_ru
            const time  = new Date(n.created_at).toLocaleDateString(lang === 'uz' ? 'uz-UZ' : 'ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
            return (
              <div key={n.id} onClick={() => onNotifClick(n)} style={{
                display: 'flex', gap: 12, padding: '12px 20px',
                borderBottom: `1px solid ${BD}`, cursor: 'pointer',
                background: n.is_read ? 'none' : 'rgba(200,155,76,0.03)',
              }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{n.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: n.is_read ? 400 : 600, color: TX, lineHeight: 1.3 }}>{title}</p>
                    {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: G, flexShrink: 0, marginTop: 3 }} />}
                  </div>
                  <p style={{ margin: '3px 0 0', fontSize: 12, color: TX2, lineHeight: 1.4 }}>{body}</p>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: TX3 }}>{time}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

// ── Label ──────────────────────────────────────────────────────
function SectionLabel({ children }: { children: string }) {
  return <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 600, color: TX3, letterSpacing: '0.12em', textTransform: 'uppercase' }}>{children}</p>
}

// ── Main ────────────────────────────────────────────────────────
export default function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const lang     = user?.language ?? 'ru'
  const tr       = t(lang)

  const [notifOpen, setNotifOpen] = useState(false)

  const { data: dash }       = useQuery({ queryKey: ['dashboard'],           queryFn: getDashboard,                    staleTime: 30_000 })
  const { data: notifs = [], isLoading: notifLoading } = useQuery({ queryKey: ['user-notifications'], queryFn: () => getUserNotifications(30), staleTime: 30_000 })
  const { data: ann }        = useQuery({ queryKey: ['latest-announcement'], queryFn: getLatestAnnouncement,           staleTime: 120_000 })
  const { data: settings }   = useQuery({ queryKey: ['settings'],            queryFn: getSettings,                     staleTime: 60_000 })
  const { data: newProds }   = useQuery({ queryKey: ['products-new'],        queryFn: () => getProducts({ limit: 6 }), staleTime: 120_000 })
  const { data: categories } = useQuery({ queryKey: ['categories'],          queryFn: getCategories,                   staleTime: 300_000 })

  const markReadMut = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-notifications'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const tier    = (dash?.tier ?? 'bronze') as keyof typeof TIER_CFG
  const cfg     = TIER_CFG[tier]
  const score   = dash?.tier_score ?? 0
  const limit   = dash?.credit_limit ?? 500
  const used    = dash?.credit_used ?? 0
  const free    = Math.max(limit - used, 0)
  const usedPct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0
  const unread  = dash?.unread_notifications ?? 0
  const usdRate = settings?.usd_rate ?? 12000
  const initial = (user?.full_name ?? '').charAt(0).toUpperCase()

  function handleNotifClick(n: UserNotification) {
    setNotifOpen(false)
    setTimeout(() => { if (n.related_order_id) navigate(`/orders/${n.related_order_id}`) }, 200)
    if (!n.is_read) markReadMut.mutate()
  }

  // News items: announcements + new products (horizontal cards)
  const newsCards = [
    ...(ann?.send_miniapp ? [{
      key: `ann-${ann.id}`,
      tag: tr('Акция', 'Aksiya'),
      tagColor: G,
      tagBg: 'rgba(200,155,76,0.15)',
      title: ann.title,
      sub: ann.body,
      image: ann.image_path ? `${API}/uploads/${ann.image_path}` : null,
      onClick: () => navigate('/orders'),
    }] : []),
    ...(newProds?.items ?? []).map(p => ({
      key: `prod-${p.id}`,
      tag: tr('Новое', 'Yangi'),
      tagColor: TX2,
      tagBg: 'rgba(168,168,176,0.14)',
      title: lang === 'uz' ? p.name_uz : p.name_ru,
      sub: `$${p.price.toLocaleString()} · ${tr("в рассрочку", "bo'lib to'lash")}`,
      image: p.image_path ? `${API}/uploads/${p.image_path}` : null,
      onClick: () => navigate(`/catalog/${p.id}`),
    })),
  ]

  const supportUrl = `https://t.me/${settings?.support_username ?? 'ansor22_support'}`

  const quickActions = [
    { icon: '💳', label: tr('Оплатить', "To'lash"),      to: '/orders' },
    { icon: '📋', label: tr('График', 'Jadval'),          to: '/orders' },
    { icon: '💬', label: tr('Поддержка', 'Qo\'llab'),    to: 'support' },
    { icon: '🧾', label: tr('История', 'Tarix'),          to: '/payments' },
  ]

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ───────────────────────────────────────────── */}
      <header className="mobile-only" style={{
        position: 'sticky', top: 0, zIndex: 40,
        padding: '10px 18px',
        background: 'rgba(13,21,38,0.82)',
        backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
        borderBottom: `1px solid ${BD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* Brand */}
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: TX, letterSpacing: '0.04em', lineHeight: 1.1 }}>ANSOR</p>
          <p style={{ margin: 0, fontSize: 9, fontWeight: 500, color: G, letterSpacing: '0.14em', textTransform: 'uppercase' }}>NASIYA SAVDO</p>
        </div>
        {/* Exchange rate */}
        <p style={{ margin: 0, fontSize: 11, color: TX3, fontFamily: MONO }}>
          {tr('Курс', 'Kurs')} · $1 = {usdRate.toLocaleString('ru-RU')} {tr('сум', "so'm")}
        </p>
        {/* Bell */}
        <button
          onClick={() => setNotifOpen(true)}
          style={{ position: 'relative', background: 'rgba(255,255,255,0.05)', border: `1px solid ${BD}`, borderRadius: '50%', width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={unread > 0 ? G : TX3} strokeWidth="1.8" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          {unread > 0 && <span style={{ position: 'absolute', top: 5, right: 5, width: 8, height: 8, borderRadius: '50%', background: G, border: `1.5px solid rgba(13,21,38,0.9)` }} />}
        </button>
      </header>

      <main style={{ flex: 1, padding: '0 0 104px', overflowY: 'auto' }}>

        {/* ── Greeting row ─────────────────────────────────── */}
        <div style={{ padding: '16px 18px 14px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${BD}` }}>
          <div style={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, rgba(200,155,76,0.30) 0%, rgba(200,155,76,0.10) 100%)`,
            border: `1.5px solid rgba(200,155,76,0.40)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700, color: G,
          }}>
            {initial}
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: TX3 }}>{tr('Здравствуйте,', 'Xush kelibsiz,')}</p>
            <p style={{ margin: '1px 0 0', fontSize: 16, fontWeight: 600, color: TX }}>{user?.full_name ?? ''}</p>
          </div>
        </div>

        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* ── Credit limit card ────────────────────────── */}
          <div style={{
            ...GLASS, borderRadius: 16, padding: '16px 16px 14px',
            border: `1px solid ${BD}`, position: 'relative', overflow: 'hidden',
          }}>
            {/* subtle top glow */}
            <div style={{ position: 'absolute', top: -30, right: -20, width: 120, height: 80, background: `radial-gradient(circle, rgba(200,155,76,0.10) 0%, transparent 70%)`, pointerEvents: 'none' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 9, fontWeight: 600, color: TX3, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                {tr('ДОСТУПНЫЙ ЛИМИТ', 'MAVJUD KREDIT')}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '2px 9px', borderRadius: 99,
                color: cfg.color, background: `${cfg.color}1A`, border: `1px solid ${cfg.color}44`,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <svg width="8" height="8" viewBox="0 0 8 8"><polygon points="4,0 8,8 0,8" fill={cfg.color}/></svg>
                {cfg.label}
              </span>
            </div>

            <p style={{ margin: '0 0 2px', fontFamily: MONO, fontSize: 28, fontWeight: 500, color: TX, lineHeight: 1 }}>
              ${free.toLocaleString()}
              <span style={{ fontFamily: MONO, fontSize: 13, color: TX3, fontWeight: 400, marginLeft: 8 }}>
                {tr(`из $${limit.toLocaleString()}`, `/ $${limit.toLocaleString()}`)}
              </span>
            </p>
            <p style={{ margin: '0 0 12px', fontFamily: MONO, fontSize: 11, color: TX3 }}>
              ≈ {Math.round(free * usdRate).toLocaleString('ru-RU')} {tr('сум', "so'm")}
            </p>

            {/* Progress bar */}
            <div style={{ height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 99, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${usedPct}%`, height: '100%', background: `linear-gradient(90deg, ${cfg.color}AA, ${G})`, borderRadius: 99, transition: 'width 0.6s' }} />
            </div>

            {/* CTA row */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => navigate('/custom-order')}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 10,
                  background: 'rgba(200,155,76,0.12)', border: `1.5px solid rgba(200,155,76,0.45)`,
                  color: G, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {tr('Оформить рассрочку', "Nasiya rasmiylashtirish")}
              </button>
              <button
                onClick={() => navigate('/catalog')}
                style={{ width: 40, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${BD}`, color: TX3, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </button>
            </div>
          </div>

          {/* ── Tier card ────────────────────────────────── */}
          <div
            onClick={() => navigate('/profile')}
            style={{ ...GLASS, borderRadius: 14, padding: '14px 16px', border: `1px solid ${BD}`, cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${cfg.color}18`, border: `1px solid ${cfg.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cfg.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 9, fontWeight: 600, color: TX3, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  {tr('ВАШ УРОВЕНЬ', 'DARAJANGIZ')}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 16, fontWeight: 600, color: cfg.color }}>{cfg.label}</p>
              </div>
              {cfg.next && (
                <span style={{ fontFamily: MONO, fontSize: 11, color: TX3 }}>
                  {score.toFixed(0)}/{cfg.nextScore} · {tr(`до ${cfg.next}`, `${cfg.next}gacha`)}
                </span>
              )}
            </div>
            <div style={{ height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{ width: `${cfg.next ? Math.min((score / (cfg.nextScore || 1)) * 100, 100) : 100}%`, height: '100%', background: `linear-gradient(90deg, ${cfg.color}88, ${cfg.color})`, borderRadius: 99 }} />
            </div>
          </div>

          {/* ── Next payment ─────────────────────────────── */}
          {dash?.next_payment && (() => {
            const np = dash.next_payment
            const overdue = np.is_overdue
            const color = overdue ? '#E05E5E' : G
            const due = new Date(np.due_date)
            const dueStr = due.toLocaleDateString(lang === 'uz' ? 'uz-UZ' : 'ru-RU', { day: 'numeric', month: 'short' })
            return (
              <div
                onClick={() => navigate(`/orders/${np.order_id}`)}
                style={{ ...GLASS, borderRadius: 14, padding: '14px 16px', border: `1px solid ${overdue ? 'rgba(224,94,94,0.25)' : BD}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 10 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: `${color}18`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: 9, fontWeight: 600, color, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
                      {overdue ? tr('ПРОСРОЧКА', 'KECHIKISH') : tr('БЛИЖАЙШИЙ ПЛАТЁЖ', "KEYINGI TO'LOV")}
                    </p>
                    <p style={{ margin: '2px 0 0', fontFamily: MONO, fontSize: 16, fontWeight: 600, color: TX }}>
                      ${np.amount.toFixed(2)} <span style={{ fontSize: 12, color: TX3, fontWeight: 400 }}>· {dueStr}</span>
                    </p>
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); navigate(`/orders/${np.order_id}`) }}
                  style={{ padding: '8px 16px', borderRadius: 99, background: `${color}18`, border: `1px solid ${color}44`, color, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  {tr('Оплатить', "To'lash")}
                </button>
              </div>
            )
          })()}

          {/* ── News & Promos ─────────────────────────────── */}
          {newsCards.length > 0 && (
            <div>
              <SectionLabel>{tr('НОВОСТИ И АКЦИИ', 'YANGILIKLAR VA AKSIYALAR')}</SectionLabel>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginLeft: -18, paddingLeft: 18, marginRight: -18, paddingRight: 18, paddingBottom: 2 }}>
                {newsCards.map(card => (
                  <div
                    key={card.key}
                    onClick={card.onClick}
                    style={{
                      flexShrink: 0, width: 168, borderRadius: 14, overflow: 'hidden',
                      ...GLASS, border: `1px solid ${BD}`, cursor: 'pointer',
                    }}
                  >
                    {card.image ? (
                      <div style={{ height: 96, overflow: 'hidden' }}>
                        <img src={card.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ) : (
                      <div style={{ height: 96, background: 'rgba(200,155,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: 36 }}>📢</span>
                      </div>
                    )}
                    <div style={{ padding: '10px 12px 12px' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 99, color: card.tagColor, background: card.tagBg, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        {card.tag}
                      </span>
                      <p style={{ margin: '6px 0 2px', fontSize: 13, fontWeight: 600, color: TX, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {card.title}
                      </p>
                      <p style={{ margin: 0, fontSize: 11, color: TX2, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {card.sub}
                      </p>
                    </div>
                  </div>
                ))}
                <div style={{ flexShrink: 0, width: 4 }} />
              </div>
            </div>
          )}

          {/* ── Quick actions ─────────────────────────────── */}
          <div>
            <SectionLabel>{tr('БЫСТРЫЕ ДЕЙСТВИЯ', 'TEZKOR AMALLAR')}</SectionLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {quickActions.map(qa => (
                <button
                  key={qa.label}
                  onClick={() => qa.to === 'support' ? window.open(supportUrl, '_blank') : navigate(qa.to)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                    padding: '14px 4px 12px', borderRadius: 14,
                    ...GLASS, border: `1px solid ${BD}`, cursor: 'pointer',
                  }}
                >
                  <span style={{ fontSize: 22 }}>{qa.icon}</span>
                  <span style={{ fontSize: 10, color: TX2, textAlign: 'center', lineHeight: 1.3 }}>{qa.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Categories ───────────────────────────────── */}
          {categories && categories.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <SectionLabel>{tr('КАТЕГОРИИ', 'KATEGORIYALAR')}</SectionLabel>
                <button onClick={() => navigate('/catalog')} style={{ background: 'none', border: 'none', fontSize: 11, color: TX3, cursor: 'pointer', padding: 0, marginBottom: 10 }}>
                  {tr('Все →', 'Barchasi →')}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {categories.slice(0, 6).map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => navigate(`/catalog?category=${cat.id}`)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      padding: '16px 8px 14px', borderRadius: 14,
                      ...GLASS, border: `1px solid ${BD}`, cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 26 }}>{cat.emoji}</span>
                    <span style={{ fontSize: 11, color: TX2, textAlign: 'center', lineHeight: 1.2 }}>
                      {lang === 'uz' ? cat.name_uz : cat.name_ru}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </main>

      <NotifDrawer
        open={notifOpen} onClose={() => setNotifOpen(false)}
        notifs={notifs} lang={lang} unread={unread} loading={notifLoading}
        onNotifClick={handleNotifClick}
        onMarkAll={() => markReadMut.mutate()}
      />

      <BottomNav />
    </div>
  )
}
